import {t,useLanguage} from '../i18n';
// src/components/PhoneKit.jsx
// Shared professional phone UI for Loop.
// Phase 1C: PedidosYa-style country picker with local SVG flags, no external CDN.

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  LOOP_PHONE_COUNTRIES,
  cleanPhoneNationalInput,
  formatPhoneDisplay,
  getFlagEmoji,
  joinPhoneDisplay,
  parsePhoneValue,
} from "../utils/phoneUtils";
import "../styles/loopLayers.css";

const PHONE_KIT_CSS = `
.loop-phone-input,
.loop-phone-input * { box-sizing: border-box; }
.loop-phone-input { display: grid; gap: 8px; width: 100%; }
.loop-phone-label { color: #0f172a; font-size: 12px; font-weight: 900; letter-spacing: .01em; }
.loop-phone-control { position: relative; display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; width: 100%; min-height: 48px; border: 1px solid #dbe5ee; background: #fff; border-radius: 18px; overflow: visible; box-shadow: 0 10px 26px rgba(15,23,42,.045); }
.loop-phone-country { min-width: 104px; height: 46px; border: 0; border-right: 1px solid #e2e8f0; border-radius: 17px 0 0 17px; background: #f8fafc; display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 0 11px; color: #0f172a; cursor: pointer; font-weight: 950; }
.loop-phone-country:hover { background: #f1f5f9; }
.loop-phone-selected-dial { font-size: 13px; font-weight: 950; color: #0f172a; font-variant-numeric: tabular-nums; }
.loop-phone-country-chevron { color: #64748b; font-size: 12px; transform: translateY(1px); }
.loop-phone-control input { min-width: 0; height: 46px; border: 0; outline: none; padding: 0 14px; border-radius: 0 17px 17px 0; background: #fff; color: #0f172a; font-size: 15px; font-weight: 850; letter-spacing: .01em; }
.loop-phone-control input::placeholder { color: #94a3b8; font-weight: 750; }
.loop-phone-hint { color: #64748b; font-size: 12px; font-style: normal; font-weight: 700; line-height: 1.35; }
.loop-phone-flag { width: 22px; height: 16px; flex: 0 0 22px; display: inline-flex; align-items: center; justify-content: center; border-radius: 4px; overflow: hidden; background: #e2e8f0; box-shadow: 0 0 0 1px rgba(15,23,42,.08); }
.loop-phone-flag img { width: 100%; height: 100%; object-fit: cover; display: block; }
.loop-phone-flag-fallback { font-size: 13px; line-height: 1; }
.loop-phone-menu { position: absolute; top: calc(100% + 8px); left: 0; z-index: var(--loop-z-popover, 2500); width: min(360px, calc(100vw - 32px)); max-height: 380px; overflow: hidden; border: 1px solid #dbe5ee; border-radius: 22px; background: rgba(255,255,255,.98); box-shadow: 0 24px 60px rgba(15,23,42,.18); backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px); }
.loop-phone-menu.is-portal { position: fixed; z-index: var(--loop-z-nested-popover, 5200); isolation: isolate; pointer-events: auto; }
.loop-phone-menu-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 12px 12px 8px; border-bottom: 1px solid #eef2f7; }
.loop-phone-menu-title { display: grid; gap: 2px; min-width: 0; }
.loop-phone-menu-title strong { color: #0f172a; font-size: 13px; font-weight: 950; }
.loop-phone-menu-title span { color: #64748b; font-size: 11px; font-weight: 750; }
.loop-phone-menu-close { width: 34px; height: 34px; border-radius: 999px; border: 1px solid #dbe5ee; background: #fff; color: #0f172a; font-size: 18px; font-weight: 900; cursor: pointer; }
.loop-phone-search-wrap { padding: 10px 12px; border-bottom: 1px solid #eef2f7; }
.loop-phone-search { width: 100%; height: 40px; border: 1px solid #dbe5ee; border-radius: 14px; background: #f8fafc; outline: none; padding: 0 12px; font-size: 13px; font-weight: 800; color: #0f172a; }
.loop-phone-options { max-height: 286px; overflow: auto; padding: 6px; scrollbar-width: thin; }
.loop-phone-option { width: 100%; min-height: 58px; border: 0; background: transparent; border-radius: 16px; display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 12px; align-items: center; padding: 9px 10px; text-align: left; color: #0f172a; cursor: pointer; }
.loop-phone-option:hover { background: #f8fafc; }
.loop-phone-option.is-active { background: #e8f8f5; }
.loop-phone-option-copy { display: grid; gap: 2px; min-width: 0; }
.loop-phone-option-copy strong { display: block; font-size: 13px; font-weight: 950; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.loop-phone-option-copy em { display: block; font-size: 11px; font-style: normal; color: #64748b; font-weight: 850; }
.loop-phone-option-check { width: 24px; height: 24px; border-radius: 999px; background: #00675f; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 950; opacity: 0; }
.loop-phone-option.is-active .loop-phone-option-check { opacity: 1; }
.loop-phone-empty { padding: 18px 14px; color: #64748b; font-size: 13px; font-weight: 800; }
.loop-phone-display { display: inline-flex; align-items: center; gap: 8px; min-width: 0; }
.loop-phone-display-value { color: #0f172a; font-weight: 850; font-variant-numeric: tabular-nums; }
.loop-phone-verified,
.loop-phone-badge { display: inline-flex; align-items: center; justify-content: center; min-height: 22px; border-radius: 999px; padding: 0 9px; font-size: 11px; font-weight: 900; white-space: nowrap; }
.loop-phone-verified,
.loop-phone-badge.is-verified { color: #00675f; background: #e8f8f5; border: 1px solid rgba(0,103,95,.15); }
.loop-phone-badge.is-pending { color: #64748b; background: #f1f5f9; border: 1px solid #e2e8f0; }
@media (max-width: 560px) {
  .loop-phone-menu.is-portal { inset: 0 !important; width: 100vw !important; max-width: none !important; max-height: none !important; border-radius: 0 !important; border: 0 !important; box-shadow: none !important; }
  .loop-phone-control { min-height: 50px; border-radius: 18px; }
  .loop-phone-country { min-width: 98px; height: 48px; }
  .loop-phone-control input { height: 48px; font-size: 16px; }
  .loop-phone-menu { position: fixed; inset: 0; width: 100vw; max-width: none; max-height: none; border-radius: 0; border: 0; box-shadow: none; background: #fff; display: flex; flex-direction: column; }
  .loop-phone-menu-head { min-height: 56px; padding: 10px 14px; }
  .loop-phone-menu-title strong { font-size: 16px; }
  .loop-phone-menu-title span { font-size: 12px; }
  .loop-phone-menu-close { order: -1; border: 0; background: transparent; font-size: 25px; }
  .loop-phone-options { flex: 1 1 auto; max-height: none; padding: 0; }
  .loop-phone-option { min-height: 60px; border-radius: 0; padding: 11px 14px; border-bottom: 1px solid #eef2f7; }
  .loop-phone-option:hover,
  .loop-phone-option.is-active { background: #fff; }
  .loop-phone-option.is-active .loop-phone-option-copy strong { color: #00675f; }
  .loop-phone-flag { width: 24px; height: 18px; border-radius: 4px; }
}
`;

function PhoneKitStyles() {
  return <style>{PHONE_KIT_CSS}</style>;
}

function countrySearchText(country = {}) {
  return [country.label, country.countryCode, country.dialCode]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function CountryFlag({ countryCode = "PA", flagUrl = "", flagUrl2x = "", label = "" }) {
  const [failed, setFailed] = useState(false);
  const safeCode = String(countryCode || "PA").toUpperCase();
  if (failed || !flagUrl) {
    return (
      <span className="loop-phone-flag" aria-label={label || safeCode} title={label || safeCode}>
        <span className="loop-phone-flag-fallback" aria-hidden="true">{safeCode}</span>
      </span>
    );
  }
  return (
    <span className="loop-phone-flag" aria-label={label || safeCode} title={label || safeCode}>
      <img
        src={flagUrl}
        srcSet={flagUrl2x ? `${flagUrl} 1x, ${flagUrl2x} 2x` : undefined}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </span>
  );
}

export function PhoneDisplay({ value, fallback = "Sin teléfono", className = "", verified = false }) {
  useLanguage();
  const display = formatPhoneDisplay(value, "PA");
  if (!display) return <span className={className}>{t(fallback)}</span>;
  return (
    <span className={`loop-phone-display ${className}`.trim()}>
      <PhoneKitStyles />
      <span className="loop-phone-display-value">{display}</span>
      {verified ? <span className="loop-phone-verified">{t("Verificado")}</span> : null}
    </span>
  );
}

export function VerifiedPhoneBadge({ verified = false, pendingLabel = "Sin verificar", verifiedLabel = "WhatsApp verificado" }) {
  useLanguage();
  return (
    <span className={`loop-phone-badge ${verified ? "is-verified" : "is-pending"}`}>
      <PhoneKitStyles />
      {t(verified ? verifiedLabel : pendingLabel)}
    </span>
  );
}

export function PhoneInput({
  label = "WhatsApp",
  value = "",
  onChange = () => {},
  defaultCountry = "PA",
  className = "",
  required = false,
  disabled = false,
  hint = "",
}) {
  useLanguage();
  const initial = useMemo(() => parsePhoneValue(value, { defaultCountry }), [value, defaultCountry]);
  const [selectedCode, setSelectedCode] = useState(initial.countryCode || defaultCountry);
  const [dialCode, setDialCode] = useState(initial.dialCode || "+507");
  const [national, setNational] = useState(initial.nationalFormatted || "");
  const [open, setOpen] = useState(false);
  const [countryQuery, setCountryQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);
  const pickerRef = useRef(null);
  const menuRef = useRef(null);

  const selected = LOOP_PHONE_COUNTRIES.find((item) => item.countryCode === selectedCode) || LOOP_PHONE_COUNTRIES[0];
  const normalizedQuery = String(countryQuery || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
  const visibleCountries = LOOP_PHONE_COUNTRIES.filter((country) => {
    if (!normalizedQuery) return true;
    return countrySearchText(country).includes(normalizedQuery);
  });

  useEffect(() => {
    const parsed = parsePhoneValue(value, { defaultCountry });
    setSelectedCode(previous => !parsed.isValid && LOOP_PHONE_COUNTRIES.find(c=>c.countryCode===previous)?.dialCode === parsed.dialCode ? previous : parsed.countryCode || defaultCountry);
    setDialCode(parsed.dialCode || "+507");
    setNational(parsed.nationalFormatted || "");
  }, [value, defaultCountry]);

  const updateMenuPosition = useCallback(() => {
    if (typeof window === "undefined" || !pickerRef.current) return;

    const rect = pickerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const mobile = viewportWidth <= 560;

    if (mobile) {
      setMenuStyle({
        position: "fixed",
        inset: 0,
        width: "100vw",
        maxWidth: "none",
        maxHeight: "none",
        zIndex: "var(--loop-z-nested-popover, 5200)",
      });
      return;
    }

    const desiredWidth = Math.min(360, Math.max(280, viewportWidth - 32));
    const left = Math.max(16, Math.min(rect.left, viewportWidth - desiredWidth - 16));
    const belowTop = rect.bottom + 8;
    const aboveTop = rect.top - 8 - 380;
    const canOpenBelow = belowTop + 260 <= viewportHeight - 16;
    const top = canOpenBelow ? belowTop : Math.max(16, aboveTop);
    const maxHeight = Math.max(220, Math.min(380, viewportHeight - top - 16));

    setMenuStyle({
      position: "fixed",
      top,
      left,
      width: desiredWidth,
      maxHeight,
      zIndex: "var(--loop-z-nested-popover, 5200)",
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();

    const close = (event) => {
      const target = event.target;
      if (pickerRef.current && pickerRef.current.contains(target)) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setOpen(false);
    };

    const reposition = () => updateMenuPosition();

    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);

    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (open) setCountryQuery("");
  }, [open]);

  const emit = (nextDialCode, nextNational) => {
    const country = LOOP_PHONE_COUNTRIES.find((item) => item.dialCode === nextDialCode) || selected;
    const display = nextNational ? joinPhoneDisplay(nextDialCode, nextNational) : "";
    onChange(display, parsePhoneValue(display, { defaultCountry: country.countryCode || defaultCountry }));
  };

  const updateCountry = (country) => {
    setSelectedCode(country.countryCode);
    setDialCode(country.dialCode);
    setOpen(false);
    emit(country.dialCode, national);
  };

  const updateNational = (raw) => {
    const next = cleanPhoneNationalInput(raw, selected.countryCode);
    setNational(next);
    emit(dialCode, next);
  };

  const countryMenu = open ? (
    <div ref={menuRef} className="loop-phone-menu is-portal" style={menuStyle || undefined} role="listbox">
      <div className="loop-phone-menu-head">
        <div className="loop-phone-menu-title">
          <strong>{t("Selecciona tu país")}</strong>
          <span>{t("Usaremos el código para WhatsApp y verificación.")}</span>
        </div>
        <button type="button" className="loop-phone-menu-close" onClick={() => setOpen(false)} aria-label={t("Cerrar selector")}>‹</button>
      </div>
      <div className="loop-phone-search-wrap">
        <input
          className="loop-phone-search"
          value={countryQuery}
          onChange={(event) => setCountryQuery(event.target.value)}
          placeholder={t("Buscar país o código")}
          autoFocus
        />
      </div>
      <div className="loop-phone-options">
        {visibleCountries.length ? visibleCountries.map((country) => (
          <button
            key={`${country.countryCode}-${country.dialCode}-${country.label}`}
            type="button"
            className={country.countryCode === selectedCode ? "loop-phone-option is-active" : "loop-phone-option"}
            onClick={() => updateCountry(country)}
            role="option"
            aria-selected={country.countryCode === selectedCode}
          >
            <CountryFlag countryCode={country.countryCode} flagUrl={country.flagUrl} flagUrl2x={country.flagUrl2x} label={t(country.label)} />
            <span className="loop-phone-option-copy">
              <strong>{t(country.label)}</strong>
              <em>{country.dialCode}</em>
            </span>
            <span className="loop-phone-option-check" aria-hidden="true">✓</span>
          </button>
        )) : <div className="loop-phone-empty">{t("No encontramos ese país.")}</div>}
      </div>
    </div>
  ) : null;

  return (
    <label className={`loop-phone-input ${className}`.trim()}>
      <PhoneKitStyles />
      {label ? <span className="loop-phone-label">{t(label)}{required ? " *" : ""}</span> : null}
      <div className="loop-phone-control" ref={pickerRef}>
        <button type="button" className="loop-phone-country" onClick={() => setOpen((v) => !v)} disabled={disabled} aria-label={t("Seleccionar país")}>
          <CountryFlag countryCode={selected.countryCode} flagUrl={selected.flagUrl} flagUrl2x={selected.flagUrl2x} label={t(selected.label)} />
          <span className="loop-phone-selected-dial">{selected.dialCode}</span>
          <span className="loop-phone-country-chevron" aria-hidden="true">⌄</span>
        </button>
        {countryMenu && typeof document !== "undefined" ? createPortal(countryMenu, document.body) : countryMenu}
        <input
          aria-label={t(label)}
          type="tel"
          value={national}
          onChange={(event) => updateNational(event.target.value)}
          placeholder={selected.placeholder || "6123-4567"}
          inputMode="tel"
          autoComplete="tel-national"
          disabled={disabled}
        />
      </div>
      {hint ? <em className="loop-phone-hint">{t(hint)}</em> : null}
    </label>
  );
}
